/**
 * The plan holder's journey (PlanMemberJourney) without React: how the
 * backend's question model maps onto the shared journey components, and how
 * their answers go back out as the bridge submission's field_values.
 *
 * The answers are held in the questionnaire's own vocabulary — the one
 * BridgeQuestionnaire uses and `emitQuestionnaireAnswers` reads — so the plan
 * holder's answers reach the Mac in exactly the shape the patient's do. The
 * shared GapCarousel speaks in option LABELS; `toGapValue` / `fromGapValue`
 * translate at that boundary only.
 */
import type { BridgeQuestion, SchemaField } from "../types/shared";
import type { GapGroup, GapQuestion } from "../shared/lib/gapCarousel";
import { J } from "../shared/ui/journey/journeyTheme";
import { FORMATS, formatValue, inferInputFormat } from "../shared/lib/inputFormats";
import { emitQuestionnaireAnswers } from "./bridgeQuestionnaire";
import { displayValue, factGroupTitle, factLabel, groupKnownQuestions, isAnsweredValue } from "./questionnaireSections";
import { resolveSignatureDateField, untouchedBaseline, type SignatureLine } from "./bridgeSchema";

export type AnswerValue = string | string[];
export type Answers = Record<string, AnswerValue>;

// ── Dates ────────────────────────────────────────────────────────────────

/** Today as yyyy-mm-dd from LOCAL date parts: toISOString() is UTC and
 *  signs an evening in Halifax with tomorrow's date. ISO because a
 *  month-first string is ambiguous to the Mac's normalizer. */
export function isoLocalDate(d: Date = new Date()): string {
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${mm}-${dd}`;
}

// Keys a session might carry the LINK's expiry under. The client session's
// own `_expiresAt` is a 24-hour token TTL, not when the request lapses, so it
// is deliberately not one of them.
const EXPIRY_KEYS = ["assignmentExpiresAt", "linkExpiresAt", "expiresAt"] as const;

/** Whole days until the link expires (rounded up), or null when the session
 *  does not say or the moment has passed — the welcome line is then omitted
 *  rather than guessed. */
export function expiryDays(session: Record<string, unknown> | null | undefined, now: Date = new Date()): number | null {
  if (!session) return null;
  for (const k of EXPIRY_KEYS) {
    const raw = session[k];
    if (raw == null || raw === "") continue;
    const t = typeof raw === "number" ? raw : Date.parse(String(raw));
    if (!Number.isFinite(t)) continue;
    const ms = t - now.getTime();
    if (ms <= 0) return null;
    return Math.ceil(ms / 86_400_000);
  }
  return null;
}

export function expiryLabel(days: number): string {
  return days === 1 ? "Expires in 1 day." : `Expires in ${days} days.`;
}

// ── Questions → GapCarousel ──────────────────────────────────────────────

/** Widgets answered by picking, not typing. */
export function isChoiceWidget(q: BridgeQuestion): boolean {
  return ["yesno", "choice", "multi", "toggle"].includes(String(q.widget || ""));
}

/** The picks a choice-like question offers: what the button says, and what
 *  the questionnaire stores for it (an option's target, or yes/no/na). */
export function choiceOptions(q: BridgeQuestion): Array<{ label: string; value: string }> {
  switch (q.widget) {
    case "yesno":
      return [
        { label: "Yes", value: "yes" },
        { label: "No", value: "no" },
        ...(q.targets?.na ? [{ label: "Not applicable", value: "na" }] : []),
      ];
    case "toggle":
      return [
        { label: "Yes", value: "yes" },
        { label: "No", value: "no" },
      ];
    case "choice":
    case "multi": {
      // GapCarousel keys its buttons and its selection by label, so two
      // options printed alike would select together; the first one wins.
      const seen = new Set<string>();
      const out: Array<{ label: string; value: string }> = [];
      for (const o of q.options || []) {
        const label = String(o.label || o.target);
        if (seen.has(label)) continue;
        seen.add(label);
        out.push({ label, value: o.target });
      }
      return out;
    }
    default:
      return [];
  }
}

function formatFor(q: BridgeQuestion): string | null {
  const fmt =
    (q.input_format as string | undefined) ||
    inferInputFormat({ name: q.target, input_format: q.input_format, widget: q.widget, type: q.type });
  return fmt && FORMATS[fmt] ? fmt : null;
}

function gapTypeFor(q: BridgeQuestion, fmt: string | null): GapQuestion["type"] {
  if (isChoiceWidget(q)) return "choice";
  if (fmt === "phone_na") return "tel";
  if (fmt === "email") return "email";
  if (fmt === "date_iso") return "date";
  if (fmt) return "text";
  switch (q.widget) {
    case "date":
    case "tel":
    case "email":
    case "number":
      return q.widget;
    default:
      // textarea and attachment questions become a one-line answer for now.
      return "text";
  }
}

export function toGapQuestion(q: BridgeQuestion): GapQuestion {
  const fmt = formatFor(q);
  const type = gapTypeFor(q, fmt);
  const out: GapQuestion = {
    key: String(q.target),
    stem: String(q.label || q.stem || q.target),
    type,
    optional: !q.required,
  };
  if (type === "choice") out.options = choiceOptions(q).map((o) => o.label);
  else if (fmt && type !== "date") out.placeholder = FORMATS[fmt].placeholder;
  return out;
}

// The person is asked about themselves before where to reach them, and the
// plan numbers last, where the benefits-card reader sits beside them.
const GAP_GROUP_ORDER = ["About you", "How to reach you", "Your plan", "Other details"];

/**
 * The carousel's cards for the questions still to answer (Michael,
 * 2026-10-09: "bunch together address, first, last name, sex, date of
 * birth"). Grouped by the same target-name rule as the known-fact blocks, so
 * a question lands under the heading its answer will later sit under; within
 * a group the backend's order is kept.
 */
export function gapGroups(toAnswer: BridgeQuestion[]): GapGroup[] {
  const buckets = new Map<string, string[]>();
  for (const q of toAnswer) {
    const title = factGroupTitle(q.target);
    buckets.set(title, [...(buckets.get(title) || []), String(q.target)]);
  }
  const order = [...GAP_GROUP_ORDER, ...Array.from(buckets.keys()).filter((t) => !GAP_GROUP_ORDER.includes(t))];
  return order.filter((t) => buckets.has(t)).map((title) => ({ key: title, title, keys: buckets.get(title) as string[] }));
}

/** The questionnaire's stored value as the carousel shows it. */
export function toGapValue(q: BridgeQuestion, v: AnswerValue | undefined): string {
  if (v === undefined) return "";
  if (isChoiceWidget(q)) {
    const want = Array.isArray(v) ? v[0] : v;
    return choiceOptions(q).find((o) => o.value === want)?.label ?? "";
  }
  return Array.isArray(v) ? v.join(", ") : String(v);
}

/** What the carousel handed back, in the questionnaire's vocabulary. A
 *  multi-select is answered with one pick here (the carousel is
 *  single-choice), stored as a one-item list so the emit logic still sees it. */
export function fromGapValue(q: BridgeQuestion, raw: string): AnswerValue {
  if (isChoiceWidget(q)) {
    const value = choiceOptions(q).find((o) => o.label === raw)?.value ?? "";
    if (q.widget === "multi") return value ? [value] : [];
    return value;
  }
  return raw;
}

/** Typed text in the shape the patient's masked inputs store (formatted
 *  phone, postal code…), applied once on the way out so the carousel's
 *  plain inputs never fight the caret. */
export function normalizeTyped(q: BridgeQuestion, raw: string): string {
  const fmt = formatFor(q);
  if (!fmt || !raw) return raw;
  const f = formatValue(fmt, raw);
  return f == null ? raw : String(f);
}

// ── Known facts → FactBlocks ─────────────────────────────────────────────

const GROUP_ACCENTS: Record<string, string> = {
  "Your plan": J.teal,
  "About you": J.sage,
  "How to reach you": J.amber,
  "Other details": J.muted,
};

export interface KnownFactRow {
  key: string;
  label: string;
  value: string;
  question: BridgeQuestion;
}

export interface KnownFactGroup {
  key: string;
  title: string;
  accent: string;
  /** Typed facts: edited in place by FactBlocks. */
  rows: KnownFactRow[];
  /** Picked facts (sex, language…): changed with an inline choice row. */
  choices: KnownFactRow[];
}

export function valueOf(q: BridgeQuestion, answers: Answers): AnswerValue {
  const a = answers[String(q.target)];
  if (a !== undefined) return a;
  return (q.value as AnswerValue) ?? "";
}

/** The known facts in the same groups, order and labels BridgeQuestionnaire
 *  shows, with each group's accent. */
export function knownFactGroups(
  known: BridgeQuestion[],
  fields: ReadonlyArray<{ name?: unknown; label?: unknown }>,
  answers: Answers,
): KnownFactGroup[] {
  return groupKnownQuestions(known).map((g) => {
    const rows: KnownFactRow[] = [];
    const choices: KnownFactRow[] = [];
    for (const q of g.items) {
      const row = {
        key: String(q.target),
        label: factLabel(q, fields),
        value: displayValue(q, valueOf(q, answers)) || "—",
        question: q,
      };
      (isChoiceWidget(q) ? choices : rows).push(row);
    }
    return { key: g.title, title: g.title, accent: GROUP_ACCENTS[g.title] || J.muted, rows, choices };
  });
}

/** Required questions still empty — the sign step waits for these, as the
 *  questionnaire's Continue always has. */
export function missingRequired(questions: BridgeQuestion[], answers: Answers): BridgeQuestion[] {
  return questions.filter((q) => !!q.required && !isAnsweredValue(valueOf(q, answers)));
}

// ── The submission ───────────────────────────────────────────────────────

// When the schema names no signature line (v1 payloads), the plan member's
// line is written under its canonical machine name.
const FALLBACK_SIGNATURE = "plan_member_signature";
const FALLBACK_SIGNATURE_DATE = "plan_member_signature_date";

/**
 * field_values for the plan holder's submission:
 *  - every answer through the questionnaire's emit logic (hidden
 *    conditionals blanked), minus anything equal to what the schema arrived
 *    with, so an untouched pre-fill never round-trips onto the canonical;
 *  - the boxes filled from the first page's plan answers (`from_first_page`),
 *    even untouched: nothing else writes them onto the form, so leaving them
 *    out would fax a blank the reader saw as "on file" (imp_5559e114);
 *  - the typed name on each of their signature lines BY NAME, with today's
 *    ISO date in the date box paired with each (imp_31e369a1).
 */
export function buildPlanMemberFieldValues(opts: {
  questions: BridgeQuestion[];
  hidden: BridgeQuestion[];
  answers: Answers;
  schemaFields: SchemaField[] | any[];
  signatureLines: SignatureLine[];
  signatureName: string;
  today: string;
}): Record<string, string> {
  const initial = untouchedBaseline(opts.schemaFields);
  const emitted = emitQuestionnaireAnswers(opts.questions, opts.hidden, opts.answers);
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(emitted)) {
    const s = Array.isArray(v) ? v.join(", ") : String(v ?? "");
    if (initial[k] !== undefined && initial[k] === s) continue;
    out[k] = s;
  }
  // The first page's answers, untouched. An answer given on this leg wins,
  // and so does a hidden question's blank.
  for (const f of opts.schemaFields as any[]) {
    if (f && f.from_first_page && !(f.name in emitted) && f.value != null && f.value !== "") {
      out[f.name] = String(f.value);
    }
  }
  const name = opts.signatureName.trim();
  if (!name) return out;
  if (opts.signatureLines.length === 0) {
    out[FALLBACK_SIGNATURE] = name;
    out[FALLBACK_SIGNATURE_DATE] = opts.today;
    return out;
  }
  for (const line of opts.signatureLines) {
    out[line.name] = name;
    out[resolveSignatureDateField(line.name, opts.schemaFields)] = opts.today;
  }
  return out;
}
